import EventTypeUser from 'domains/models/event_type_user';
import EventTypeService from 'domains/models/event_type_service';
import * as UserSchema from 'presentations/api/schema/user';
import * as ServiceSchema from 'presentations/api/schema/service';
import {
  LocationType,
  defaultSort,
} from 'domains/models/event_type/locationType';
import {
  DateTime,
  formatToEXDATE,
  formatToMills,
  formatToSeconds,
  formatToYMD,
  getDateTime,
} from 'infras/datetime';
import EventTypeCalendar from 'domains/models/event_type_calendar';
import User from 'domains/models/user';
import { PeriodType } from 'domains/models/event_type/periodType';
import {
  AdminCalendarEventItem,
  AdminCalendarEventListItem,
  TimeListViewerCalendarEvent,
  UnavailableInfomation,
  ViewerCalendarEventListItem,
} from '../schema/calendar_event';
import {
  CalendarSourceType,
  CalendarUnavailableReasonType,
} from '../schema/calendar_event/enum';
import { EventTypeItem } from '../schema/event_type';
import {
  AdminEventTypeUser,
  EventTypeUserListItem,
} from '../schema/event_type_user';
import { FrequencyType } from 'domains/models/event_type_calendar/frequencyType';

import holidays2023 from 'usecases/app_calendar/jp/holidays/2023.json';
import holidays2024 from 'usecases/app_calendar/jp/holidays/2024.json';
import { AvailableUserType } from 'domains/models/event_type/availableUserType';
import { EventTypeServiceListItem } from '../schema/event_type_service';
import Service from 'domains/models/service';
import ServicePrice from 'domains/models/service_price';
import TaxRate from 'domains/models/tax_rate';
import { Transparency } from 'domains/models/event/transparency';
import { RestrictPeriod } from 'domains/models/event_type_restrict/restrictPeriod';
import EventTypeRestrict from 'domains/models/event_type_restrict';
import EventTypeRestrictValidator, {
  RestrictDurationCheckEvent,
} from 'domains/models/event_type_restrict/validator';
import { EventTypeVersion } from 'domains/models/event_type/eventTypeVersion';
import { ResourceScheduleItem } from 'usecases/event_type_schedule';
import ServiceRestrictSetting from 'domains/models/service_restrict_setting';
import ServiceRestrict from 'domains/models/service_restrict';
import { toItem } from '../schema/service_restrict_setting';
import {
  GracePeriodPoint,
  GracePeriodUnit,
} from 'domains/models/event_type/gracePeriodType';
import {
  UserType,
  UserTypeText,
} from 'domains/models/event_type_user/user_type';
import { Role, RoleText } from 'domains/models/event_type_user/role';

export const eventTypeSharedUsers = (
  eventTypeUsers: EventTypeUser[],
  eventTypeId: number,
  users: User[],
) => {
  const sharedUsers: EventTypeUserListItem[] = [];
  for (const eventTypeUser of eventTypeUsers) {
    if (eventTypeUser.eventTypeId !== eventTypeId) continue;
    const user = users.find((u) => u.uid === eventTypeUser.userId);
    if (!user) continue;
    const listItem: EventTypeUserListItem = {
      ...UserSchema.toListItem(user),
      role: eventTypeUser.role,
      userType: eventTypeUser.userType,
      sort: eventTypeUser.sort,
      priority: eventTypeUser.priority,
    };
    sharedUsers.push(listItem);
  }
  return sharedUsers;
};

export const adminEventTypeSharedUsers = (
  eventTypeUsers: EventTypeUser[],
  eventTypeId: number,
  users: User[],
) => {
  const sharedUsers: AdminEventTypeUser[] = [];
  for (const eventTypeUser of eventTypeUsers) {
    if (eventTypeUser.eventTypeId !== eventTypeId) continue;
    const user = users.find((u) => u.uid === eventTypeUser.userId);
    if (!user) continue;
    const listItem: AdminEventTypeUser = {
      uid: user.uid,
      name: user.name,
      profileImage: user.profileImage,
      email: user.email!,
      role: eventTypeUser.role,
      userType: eventTypeUser.userType,
      sort: eventTypeUser.sort,
    };
    sharedUsers.push(listItem);
  }
  return sharedUsers;
};

export const availableLocationIds = (eventType: EventTypeItem) => {
  let locationIds: LocationType[] = [];
  const location = eventType.location;
  if (location) {
    if (location.sort) {
      const sortList: LocationType[] = location.sort.split(',').map(Number);
      locationIds = sortList.filter((sort) => {
        const locationType: LocationType = Number(sort);
        switch (locationType) {
          case LocationType.InPerson:
            return location.isInPerson;
          case LocationType.Zoom:
            return location.isZoom;
          case LocationType.GoogleMeet:
            return location.isGoogle;
          case LocationType.Teams:
            return location.isTeams;
          case LocationType.Phone:
            return location.isPhone;
          case LocationType.AskInvitee:
            return location.isAskInvitee;
          case LocationType.Custom:
            return location.isCustom;
          default:
            return false;
        }
      });
    }

    // sortにはないがフラグがONの場合に有効リストに追加する
    defaultSort.forEach((e) => {
      if (!locationIds.includes(e)) {
        if (
          (location.isInPerson && e === LocationType.InPerson) ||
          (location.isGoogle && e === LocationType.GoogleMeet) ||
          (location.isZoom && e === LocationType.Zoom) ||
          (location.isTeams && e === LocationType.Teams) ||
          (location.isPhone && e === LocationType.Phone) ||
          (location.isAskInvitee && e === LocationType.AskInvitee) ||
          (location.isCustom && e === LocationType.Custom)
        ) {
          locationIds.push(e);
        }
      }
    });
  }
  return locationIds;
};

export const eventTypeSharedServices = (
  eventTypeServices: EventTypeService[],
  eventTypeId: number,
  services: Service[],
  servicePrices: ServicePrice[],
  taxRates: TaxRate[],
  serviceRestrictSettings: ServiceRestrictSetting[],
  serviceRestricts: ServiceRestrict[],
) => {
  const sharedServices: EventTypeServiceListItem[] = [];
  for (const eventTypeService of eventTypeServices) {
    if (eventTypeService.eventTypeId !== eventTypeId) continue;
    const service = services.find((s) => s.id === eventTypeService.serviceId);
    const price = servicePrices.find(
      (p) => p.serviceId === eventTypeService.serviceId,
    );
    const taxRate = ServicePrice.getTaxRate(price, taxRates);
    const restrictSetting = serviceRestrictSettings.find(
      (p) => p.serviceId === eventTypeService.serviceId,
    );
    const restricts = serviceRestricts.filter(
      (p) => p.serviceId === eventTypeService.serviceId,
    );

    if (!service) continue;
    const listItem: EventTypeServiceListItem = {
      ...ServiceSchema.toListItem(service),
      serviceId: service.id,
      isRequired: eventTypeService.isRequired,
      sort: eventTypeService.sort,
      price: price ? price : null,
      taxRate: taxRate ? taxRate : null,
      restrictSetting: restrictSetting ? toItem(restrictSetting) : null,
      restricts,
    };
    sharedServices.push(listItem);
  }
  return sharedServices;
};

export interface CalendarDateDetail {
  date: string;
  timeList: TimeList;
}

interface GetAllWeeklyTimeList {
  eventType: EventTypeItem;
  weekDays: DateTime[];
  timeZone: string;
  eventTypeCalendars: EventTypeCalendar[];
  isHostDiagnosticMode?: boolean;
}
interface GetAllTimeList {
  eventType: EventTypeItem;
  selectedDateTime: DateTime;
  timeZone: string;
  eventTypeCalendars: EventTypeCalendar[];
  isHostDiagnosticMode?: boolean;
}

interface GetTimeList {
  eventType: EventTypeItem;
  isIncludeAllDay: boolean;
  isExcludeHoliday: boolean;
  selectedDateTime: DateTime;
  timeZone: string;
  eventTypeCalendars: EventTypeCalendar[];
  blocks: AdminCalendarEventItem[];
  viewerBlocks?: ViewerCalendarEventListItem[];
  resourceSchedules: ResourceScheduleItem[];
  periodStart: number;
  periodEnd: number | null;
  bufferAfter: number;
  bufferBefore: number;
  isBufferResource: boolean;
  isHostDiagnosticMode?: boolean;
  expand?: string[];
  eventTypeRestricts?: EventTypeRestrict[];
  restrictCheckEvents?: RestrictDurationCheckEvent[];
  editingEventUid?: string | null;
}

interface LocalEventTypeCalendars {
  id: number;
  startedAt: Date;
  endedAt: Date | null;
  startHour: number;
  startMinute: number;
  endHour: number;
  endMinute: number;
  wday: number;
  frequencyType: FrequencyType | null;
  frequencyInterval: number | null;
  parentEventTypeCalendarId: number | null;
  exceptions: string[] | null;
}

export const getAvailableDays = (
  dateList: CalendarDateDetail[],
  currentDate: DateTime,
  periodEnd: DateTime,
  isHostDiagnosticMode: boolean,
) => {
  const days = [];
  let firstAvailableDate: DateTime | undefined;
  const today = getDateTime().startOf('day');
  while (formatToSeconds(currentDate) < formatToSeconds(periodEnd)) {
    if (currentDate < today) {
      days.push(false);
      currentDate = currentDate.plus({ day: 1 });
      continue;
    }
    const isAvailable = isAvailableDay(
      dateList,
      currentDate,
      isHostDiagnosticMode,
    );
    days.push(isAvailable);
    if (isAvailable && !firstAvailableDate) {
      firstAvailableDate = currentDate;
    }
    currentDate = currentDate.plus({ day: 1 });
  }

  let firstAvailableTimeList: TimeList = [];
  if (firstAvailableDate) {
    const date: CalendarDateDetail | undefined = dateList.find(
      (calendarDate: CalendarDateDetail) =>
        calendarDate.date === formatToYMD(firstAvailableDate!),
    );
    if (date) {
      firstAvailableTimeList = date.timeList;
    }
  }

  return {
    days,
    firstAvailableDate,
    firstAvailableTimeList,
  };
};

const isAvailableDay = (
  dateList: CalendarDateDetail[],
  currentDate: DateTime,
  isHostDiagnosticMode: boolean,
) => {
  let isAvailable = false;
  if (isHostDiagnosticMode) {
    isAvailable = dateList.some(
      (c: CalendarDateDetail) =>
        c.date === formatToYMD(currentDate) &&
        c.timeList.some((t) => t[5] === null),
    );
  } else {
    isAvailable = dateList.some(
      (c: CalendarDateDetail) => c.date === formatToYMD(currentDate),
    );
  }
  return isAvailable;
};

export const calcRollingDaysPeriodEnd = (
  periodDuration: number,
  timeZone: string,
) => {
  const durationDays = periodDuration / 60 / 24;
  return getDateTime()
    .setZone(timeZone)
    .plus({ day: durationDays })
    .endOf('day');
};

export const calcDateRangePeriodStart = (
  periodStart: string,
  timeZone: string,
) => {
  return getDateTime(periodStart, timeZone).startOf('day');
};

export const calcDateRangePeriodEnd = (periodEnd: string, timeZone: string) => {
  return getDateTime(periodEnd, timeZone).startOf('day').plus({ day: 1 });
};

export const processLimitValues = (
  eventType: EventTypeItem,
  timeZone: string,
) => {
  let bufferBefore: number = 0;
  if (eventType.bufferBefore) {
    bufferBefore = eventType.bufferBefore * 60 * 1000;
  }
  let bufferAfter: number = 0;
  if (eventType.bufferAfter) {
    bufferAfter = eventType.bufferAfter * 60 * 1000;
  }

  let periodStart: number | null = null;
  let periodEnd: number | null = null;
  if (eventType.periodType === PeriodType.RollingDays) {
    if (eventType.periodDuration !== null) {
      periodStart = formatToMills();
      periodEnd = formatToMills(
        calcRollingDaysPeriodEnd(eventType.periodDuration, eventType.timeZone),
      );
    }
  } else if (eventType.periodType === PeriodType.DateRange) {
    if (eventType.periodStart) {
      periodStart = calcDateRangePeriodStart(
        eventType.periodStart,
        eventType.timeZone,
      ).toMillis();
    }
    if (eventType.periodEnd) {
      // 終了日の23:59まで有効にする
      periodEnd = formatToMills(
        calcDateRangePeriodEnd(eventType.periodEnd, eventType.timeZone),
      );
    }
  }

  const now = getDateTime().toUTC();
  let graceStart = now.toMillis();
  if (eventType.isLimitGracePeriod && 0 < eventType.gracePeriod) {
    let start = getDateTime();
    if (eventType.gracePeriodPoint === GracePeriodPoint.Today) {
      start = getDateTime()
        .setZone(eventType.timeZone)
        .set({ hour: 0, minute: 0, second: 0 });
    }

    if (eventType.gracePeriodUnit === GracePeriodUnit.Day) {
      start = start.plus({ day: eventType.gracePeriod });
    } else if (eventType.gracePeriodUnit === GracePeriodUnit.Minute) {
      start = start.plus({ minute: eventType.gracePeriod });
    }

    graceStart = start.toUTC().toMillis();
  }
  if (periodStart === null) {
    periodStart = graceStart;
  } else {
    if (periodStart < graceStart) {
      periodStart = graceStart;
    }
  }
  return {
    bufferBefore,
    bufferAfter,
    periodStart,
    periodEnd,
  };
};

const oneMinMilSec = 1000 * 60;
const oneDayMilSec = oneMinMilSec * 60 * 24;

/**
 * 週ビュー用の有効な時間リストを返します。
 * 週の中の一番早い時間と遅い時間を基準に四角い時間リストを作成します。
 */
export const getAllWeeklyTimeList = (props: GetAllWeeklyTimeList) => {
  const allTimeList = props.weekDays
    // 曜日毎の候補時間リストをフラットに並べ取得 [[ 15, 30 ], [ 16, 30 ], [ 16, 0 ], [ 16, 30 ]]
    .flatMap((day) =>
      getAllTimeList({
        eventType: props.eventType,
        selectedDateTime: day,
        timeZone: props.timeZone,
        eventTypeCalendars: props.eventTypeCalendars,
        isHostDiagnosticMode: props.isHostDiagnosticMode,
      }),
    )
    // 重複時間を除外 [[ 15, 30 ], [ 16, 30 ], [ 16, 0 ]]
    .reduce((accumulator: number[][], current: number[]) => {
      if (
        !accumulator.some((item) => {
          return item.every((val, index) => val === current[index]);
        })
      ) {
        accumulator.push(current);
      }
      return accumulator;
    }, [])
    // 時間の昇順でソート [[ 15, 30 ], [ 16, 0 ], [ 16, 30 ]]
    .sort((a, b) => (a[0] === b[0] ? a[1] - b[1] : a[0] - b[0]));

  const result: CalendarDateDetail[] = props.weekDays.map((wday) => {
    return {
      date: formatToYMD(wday),
      timeList: (allTimeList.length > 0
        ? allTimeList
        : [10, 11, 12, 13, 14].map((i) => [i, 0])) as TimeListItem[], // ダミーの表示用
    };
  });

  return result;
};

/**
 * 指定された日付の予定タイプとして有効な時間のリストを返します。
 * getTimeListとは違い、予定(blocksとviewerblocks)によるブロックやperiodによる期間を反映しない、純粋に有効な時間リストです。
 */
const getAllTimeList = (props: GetAllTimeList) => {
  const timeList: Array<number[]> = [];

  // ユーザーに変更されないTZ
  const eventTypeTimeZone = props.eventType.timeZone;
  // FEで選択したTZオフセット - DB(EventType.timeZone)に保存されているTZオフセット
  const offsetMills =
    (props.selectedDateTime.setZone(props.timeZone).offset -
      props.selectedDateTime.setZone(eventTypeTimeZone).offset) *
    60 *
    1000;

  const intervalMinutes = props.eventType.intervalMinutes || 30;

  // eventTypeCalendarをlocaltimeに変換する
  const eventTypeCalendars = props.eventTypeCalendars.filter(
    (c) => c.endedAt === null || formatToSeconds(c.endedAt) > formatToSeconds(),
  );
  const localEventTypeCalendars: LocalEventTypeCalendars[] = processToLocaltime(
    props.eventType.intervalMinutes,
    eventTypeCalendars,
    offsetMills,
    props.timeZone,
    props.eventType.timeZone,
    props.selectedDateTime,
  );

  const availableRanges = getAvailableRange(
    props.selectedDateTime,
    props.timeZone,
    localEventTypeCalendars,
  );
  if (availableRanges.length === 0) {
    return timeList;
  }

  const slots: { hour: number; minute: number }[] = [];
  availableRanges.forEach((range) => {
    getTimeSlots(range, intervalMinutes, props.eventType.version).forEach(
      (slot) => {
        if (
          slots.findIndex(
            (s) => s.hour === slot.hour && s.minute === slot.minute,
          ) === -1
        ) {
          slots.push(slot);
        }
      },
    );
  });

  for (const slot of slots) {
    const item = [slot.hour, slot.minute];
    timeList.push(item);
  }

  return timeList;
};

/**
 * timeList
 * [0]: Hour
 * [1]: Minute
 * [2]: TimeListViewerCalendarEvent
 * [3]: RemainingCapacity
 * [4]: GuestUser List
 * [5]: UnavailableInfomation List
 * [6]: Transparency
 * [7]: AvailableUser List
 * [8]: AvailableResource List
 */
export type TimeListItem = [
  number,
  number,
  TimeListViewerCalendarEvent | null,
  number | null,
  UserSchema.SharedGuestUserItem[] | null,
  UnavailableInfomation[] | null,
  Transparency | null,
  string[],
  number[],
];
export type TimeList = TimeListItem[];

export const getTimeList = (props: GetTimeList): TimeList => {
  const timeList: TimeList = [];
  // UIで指定したTZ
  const nowLocal = getDateTime().setZone(props.timeZone);

  // TZがマイナスの場合は1日を足す
  const selectedDateStartedAt =
    nowLocal.offset >= 0
      ? props.selectedDateTime.setZone(props.timeZone).startOf('day')
      : props.selectedDateTime
          .setZone(props.timeZone)
          .startOf('day')
          .plus({ day: 1 });
  // ユーザーに変更されないTZ
  const eventTypeTimeZone = props.eventType.timeZone;

  if (
    selectedDateStartedAt < nowLocal.startOf('day') ||
    selectedDateStartedAt <
      getDateTime(props.periodStart).setZone(props.timeZone).startOf('day') ||
    (props.periodEnd && selectedDateStartedAt > getDateTime(props.periodEnd))
  ) {
    return timeList;
  }

  // FEで選択したTZオフセット - DB(EventType.timeZone)に保存されているTZオフセット
  const offsetMills =
    (props.selectedDateTime.setZone(props.timeZone).offset -
      props.selectedDateTime.setZone(eventTypeTimeZone).offset) *
    60 *
    1000;

  const utcOffsetSec =
    getDateTime().toUTC().offset - nowLocal.setZone(eventTypeTimeZone).offset;

  let isHolidayBlock = false;
  if (
    props.isExcludeHoliday &&
    [2023, 2024].includes(props.selectedDateTime.year)
  ) {
    let holidayDates: string[] = [];
    if (props.selectedDateTime.year === 2023) {
      holidayDates = Object.keys(holidays2023);
    } else if (props.selectedDateTime.year === 2024) {
      holidayDates = Object.keys(holidays2024);
    }

    if (holidayDates.includes(props.selectedDateTime.toFormat('yyyy-MM-dd'))) {
      isHolidayBlock = true;
    }
  }

  const selectedStartTime = formatToMills(
    props.selectedDateTime
      .minus({
        minutes: getDateTime().setZone(props.timeZone).offset,
      })
      .setZone(props.timeZone)
      .set({
        hour: 0,
        minute: 0,
        second: 0,
        millisecond: 0,
      }),
  );
  const adminBlocks: AdminCalendarEventItem[] = [];
  props.blocks.forEach((blockData: AdminCalendarEventItem) => {
    const events = blockData.events.filter(
      (block: AdminCalendarEventListItem) => {
        return filterBlock(block, props.timeZone, selectedStartTime);
      },
    );
    adminBlocks.push({
      userId: blockData.userId,
      events,
    });
  });
  let adminBlocksGroup: AdminCalendarEventItem[] = [];
  if (props.eventType.availableUserType === AvailableUserType.AnyOne) {
    adminBlocksGroup = adminBlocks;
  } else {
    const events: AdminCalendarEventListItem[] = [];
    adminBlocks.forEach((blockData) => {
      blockData.events.forEach((event) => {
        events.push(event);
      });
    });
    adminBlocksGroup.push({
      userId: 'allUser',
      events,
    });
  }

  let viewerBlocks: ViewerCalendarEventListItem[] = [];
  if (props.viewerBlocks) {
    viewerBlocks = props.viewerBlocks.filter((block) => {
      return filterBlock(block, props.timeZone, selectedStartTime);
    });
  }

  const duration = props.eventType.duration * oneMinMilSec;
  const intervalMinutes = props.eventType.intervalMinutes || 30;

  let isToday = false;
  if (
    props.selectedDateTime.year === nowLocal.year &&
    props.selectedDateTime.month === nowLocal.month &&
    props.selectedDateTime.day === nowLocal.day
  ) {
    isToday = true;
  }

  // eventTypeCalendarをlocaltimeに変換する
  const eventTypeCalendars = props.eventTypeCalendars.filter(
    (c) => c.endedAt === null || formatToSeconds(c.endedAt) > formatToSeconds(),
  );
  const localEventTypeCalendars: LocalEventTypeCalendars[] = processToLocaltime(
    props.eventType.intervalMinutes,
    eventTypeCalendars,
    offsetMills,
    props.timeZone,
    props.eventType.timeZone,
    props.selectedDateTime,
  );
  const periodStart = formatToMills(
    getDateTime(props.periodStart).startOf('minute'),
  );
  const periodEnd = props.periodEnd;
  const isDisplayFullCapacity = props.eventType.isDisplayFullCapacity;
  const isReturnAvailableUsers = props.expand?.includes('availableUsers');
  const isReturnAvailableResources = props.expand?.includes(
    'availableResources',
  );

  const availableRanges = getAvailableRange(
    props.selectedDateTime,
    props.timeZone,
    localEventTypeCalendars,
  );
  if (availableRanges.length === 0) {
    return timeList;
  }

  const slots: { hour: number; minute: number }[] = [];
  availableRanges.forEach((range) => {
    getTimeSlots(range, intervalMinutes, props.eventType.version).forEach(
      (slot) => {
        if (
          slots.findIndex(
            (s) => s.hour === slot.hour && s.minute === slot.minute,
          ) === -1
        ) {
          slots.push(slot);
        }
      },
    );
  });

  for (const slot of slots) {
    const { hour, minute } = slot;
    // 10:00開始の例
    // [開始時間, 開始分, 自己予定名, セミナー残数, セミナー参加Users[], 理由, transparency, availableUsers]
    // [ 10, 0, 'エンジニアMTG', 3, [], [{reason: 3, users: [] },{..}], 1, ['hash1', 'hash2'], [1,2,3]]
    const item: TimeListItem = [
      hour,
      minute,
      null,
      null,
      null,
      null,
      Transparency.Opaque,
      [],
      [],
    ] as TimeListItem;

    if (isHolidayBlock) {
      if (props.isHostDiagnosticMode) {
        item[5] = [
          { reason: CalendarUnavailableReasonType.Holiday, users: [] },
        ];
        timeList.push(item);
      }
      continue;
    }

    if (isToday) {
      if (nowLocal.hour > hour) {
        if (props.isHostDiagnosticMode) {
          item[5] = rangeStatus();
          timeList.push(item);
        }
        continue;
      } else if (nowLocal.hour === hour) {
        if (nowLocal.minute > minute) {
          if (props.isHostDiagnosticMode) {
            item[5] = rangeStatus();
            timeList.push(item);
          }
          continue;
        }
      }
    }

    const current = props.selectedDateTime
      .minus({
        minutes: props.selectedDateTime.setZone(props.timeZone).offset,
      })
      .setZone(props.timeZone)
      .set({
        hour,
        minute,
        second: 0,
        millisecond: 0,
      });
    const currentStart = formatToMills(current);
    const currentEnd = currentStart + duration;
    const currentWithBufferStartTime =
      props.bufferBefore > 0 ? currentStart - props.bufferBefore : currentStart;
    const currentWithBufferEndTime = props.bufferAfter
      ? currentEnd + props.bufferAfter
      : currentEnd;

    // 予約制限
    let restrictPeriod = undefined;
    const eventTypeRestricts = props.eventTypeRestricts || [];
    for (const restrict of eventTypeRestricts) {
      const period =
        restrict.restrictPeriod === RestrictPeriod.Day
          ? 'day'
          : restrict.restrictPeriod === RestrictPeriod.Week
          ? 'week'
          : 'month';
      const isExceeded = EventTypeRestrictValidator.isEventDurationExceeded(
        restrict.value,
        formatToSeconds(new Date(currentStart)),
        formatToSeconds(new Date(currentEnd)),
        formatToSeconds(
          getDateTime(currentStart).setZone(eventTypeTimeZone).startOf(period),
        ),
        formatToSeconds(
          getDateTime(currentStart).setZone(eventTypeTimeZone).endOf(period),
        ),
        props.restrictCheckEvents || [],
        props.editingEventUid || null,
      );
      if (isExceeded) {
        restrictPeriod = restrict.restrictPeriod;
      }

      if (restrictPeriod) {
        if (props.isHostDiagnosticMode) {
          item[5] = [
            {
              reason:
                restrictPeriod === RestrictPeriod.Day
                  ? CalendarUnavailableReasonType.RestrictDay
                  : restrictPeriod === RestrictPeriod.Week
                  ? CalendarUnavailableReasonType.RestrictWeek
                  : CalendarUnavailableReasonType.RestrictMonth,
              users: [],
            },
          ];
          timeList.push(item);
        }
        break;
      }
    }
    if (restrictPeriod) {
      continue;
    }

    if (
      periodStart !== null &&
      (currentStart < periodStart || currentEnd < periodStart)
    ) {
      if (props.isHostDiagnosticMode) {
        item[5] = rangeStatus();
        timeList.push(item);
      }
      continue;
    }
    if (
      periodEnd !== null &&
      (currentStart > periodEnd || currentEnd > periodEnd)
    ) {
      if (props.isHostDiagnosticMode) {
        item[5] = rangeStatus();
        timeList.push(item);
      }
      continue;
    }
    const isAvailable = localEventTypeCalendars.some((calendar) => {
      // 対象外の曜日
      if (calendar.wday !== current.weekday % 7) return;

      // 対象外の週
      if (
        calendar.frequencyType === FrequencyType.Weekly &&
        calendar.frequencyInterval &&
        calendar.frequencyInterval > 1
      ) {
        const diffWeeks = Math.floor(
          getDateTime(currentStart).diff(
            getDateTime(calendar.startedAt),
            'weeks',
          ).weeks,
        );
        if (diffWeeks % calendar.frequencyInterval) {
          return;
        }
      }

      // 空き枠がEventTypeCalendarの開始・終了ピリオド範囲外
      const startedAt = getDateTime(
        calendar.startedAt,
        eventTypeTimeZone,
      ).setZone(props.timeZone);
      const endedAt = getDateTime(calendar.endedAt, eventTypeTimeZone).setZone(
        props.timeZone,
      );
      const start = startedAt.toMillis();
      if (currentStart < start) return;
      if (currentEnd < start) return;
      const end = endedAt.toMillis();
      if (calendar.endedAt) {
        if (currentStart > end) return;
        if (currentEnd > end) return;
      }

      // データバグで繰り返し設定ではないのにendedAtが入らない場合の特別処置
      if (
        calendar.frequencyInterval === null &&
        calendar.endedAt === null &&
        (props.selectedDateTime.year !== startedAt.year ||
          props.selectedDateTime.month !== startedAt.month ||
          props.selectedDateTime.day !== startedAt.day)
      ) {
        return;
      }

      const childEventTypeCalendars = localEventTypeCalendars.filter(
        (c: LocalEventTypeCalendars) =>
          c.parentEventTypeCalendarId === calendar.id,
      );
      for (const child of childEventTypeCalendars) {
        const childStartedAt = formatToMills(child.startedAt);
        const childEndedAt = formatToMills(child.endedAt);
        if (
          childEndedAt > currentStart &&
          currentStart >= childStartedAt &&
          childEndedAt >= currentEnd &&
          currentEnd > childStartedAt
        ) {
          return;
        }
      }

      // 除外(exceptions)に当てはまるか
      if (calendar.exceptions && calendar.exceptions.length > 0) {
        const year = props.selectedDateTime.year;
        const month = props.selectedDateTime.month;
        const day = props.selectedDateTime.day;
        for (const exception of calendar.exceptions) {
          if (
            year === getDateTime(exception).year &&
            month === getDateTime(exception).month &&
            day === getDateTime(exception).day
          ) {
            return;
          }
        }
      }

      // 空き枠がEventTypeCalendarの有効時間帯にあるか
      const startTime = formatToMills(
        props.selectedDateTime.setZone(props.timeZone).set({
          year: props.selectedDateTime.year,
          month: props.selectedDateTime.month,
          day: props.selectedDateTime.day,
          hour: calendar.startHour,
          minute: calendar.startMinute,
          second: 0,
          millisecond: 0,
        }),
      );
      const endTime = formatToMills(
        props.selectedDateTime.setZone(props.timeZone).set({
          year: props.selectedDateTime.year,
          month: props.selectedDateTime.month,
          day: props.selectedDateTime.day,
          hour: calendar.endHour,
          minute: calendar.endMinute,
          second: 0,
          millisecond: 0,
        }),
      );

      if (endTime > currentStart && currentStart >= startTime) {
        if (endTime >= currentEnd && currentEnd > startTime) {
          return true;
        } else if (props.isHostDiagnosticMode) {
          item[5] = [
            { reason: CalendarUnavailableReasonType.Duration, users: [] },
          ];
        }
      }
    });
    if (!isAvailable) {
      if (props.isHostDiagnosticMode) {
        if (!item[5]) {
          item[5] = rangeStatus();
        }
        timeList.push(item);
      }
      continue;
    }

    // 予定によるブロック
    let isAnyAvailable = adminBlocksGroup.length > 0 ? false : true;
    let isAnyResourceAvailable =
      props.resourceSchedules.length > 0 ? false : true;
    const hasCapacity =
      props.eventType.maxCapacity && props.eventType.maxCapacity > 0;
    const initialCapacity = () => {
      if (hasCapacity && props.eventType.maxCapacity) {
        return props.eventType.maxCapacity;
      }
      if (props.eventType.availableUserType === AvailableUserType.AnyOne) {
        return adminBlocksGroup.length === 0 ? 1 : adminBlocksGroup.length;
      }
      return 1;
    };
    let remainingCapacity = initialCapacity();
    const guestUsers: UserSchema.SharedGuestUserItem[] = [];
    const unavailableInfomations: UnavailableInfomation[] = [];
    if (hasCapacity) {
      const fillSeatUids: string[] = [];
      const seminarEventUids: string[] = [];
      for (const blocks of adminBlocksGroup) {
        blocks.events.forEach((block) => {
          if (
            block.calendarSourceType === CalendarSourceType.Jicoo &&
            block.eventTypeId === props.eventType.id &&
            block.isParent &&
            block.eventUid
          ) {
            seminarEventUids.push(block.eventUid);
          }
        });
        const isNotAvailable = blocks.events.some((block) => {
          if (
            block.calendarSourceType === CalendarSourceType.Integration &&
            seminarEventUids.includes(block.uid)
          ) {
            return false;
          }
          if (block.isParent) {
            return false;
          }
          if (remainingCapacity === 0) {
            return true;
          }

          const blockStartTime = Date.parse(block.startedAt);
          const blockEndTime = Date.parse(block.endedAt);
          const duration = blockEndTime - blockStartTime;
          const isAllDay = duration % oneDayMilSec === 0;
          if (isAllDay && !props.isIncludeAllDay) return false;

          const isSeminarChildEvent =
            block.calendarSourceType === CalendarSourceType.Jicoo &&
            props.eventType.id === block.eventTypeId &&
            block.isSeminar;

          // バッファなしでコマと予定が同時刻 かつ 同じ予約ページ かつ セミナーの予定 の場合シート数を１つ減らす
          if (
            currentStart === blockStartTime &&
            currentEnd === blockEndTime &&
            block.transparency !== Transparency.Transparent &&
            isSeminarChildEvent
          ) {
            if (fillSeatUids.includes(block.uid)) {
              // ホストがゲストとして自分で予約しているケースは同じEventデータが2つあるので２個目はskip
              return false;
            } else {
              fillSeatUids.push(block.uid);
              remainingCapacity -= 1;
              if (block.guestUser) {
                guestUsers.push(block.guestUser);
              }
              return false;
            }
          }

          // HostDiagnosticMode: バッファなしで日程が重なっているかcheck
          if (
            props.isHostDiagnosticMode &&
            isUnavailableSchedule(
              blockStartTime,
              blockEndTime,
              currentStart,
              currentEnd,
            )
          ) {
            if (
              block.unavailableInfomation &&
              block.transparency !== Transparency.Transparent
            ) {
              unavailableInfomations.push(block.unavailableInfomation);
            }
            if (block.transparency === Transparency.Transparent) {
              item[2] = convertBlogkToTransparentCalendarEvent(block);
              item[6] = block.transparency;
            }
            return true;
          }

          // バッファ込みのコマが別の予定と被っているか
          if (
            !block.isBuffer &&
            isUnavailableSchedule(
              blockStartTime,
              blockEndTime,
              currentWithBufferStartTime,
              currentWithBufferEndTime,
            ) &&
            block.transparency !== Transparency.Transparent
          ) {
            if (props.isHostDiagnosticMode) {
              unavailableInfomations.push({
                reason: CalendarUnavailableReasonType.Buffer,
                users: [],
              });
            }
            return true;
          }
        });

        if (!isNotAvailable || remainingCapacity === 0) {
          isAnyAvailable = true;
          break;
        }
      }

      // シート数はリソースでは関係ない。同じ予約ページのgoogle予定は1つにまとめるのみ
      for (const resourceSchedule of props.resourceSchedules) {
        const isNotAvailable = resourceSchedule.events.some((block) => {
          // 同じ予約ページで作られたセミナー用の予定はブロックしない
          if (seminarEventUids.includes(block.uid)) {
            return false;
          }
          const blockStartTime = Date.parse(block.startedAt);
          const blockEndTime = Date.parse(block.endedAt);
          const duration = blockEndTime - blockStartTime;
          const isAllDay = duration % oneDayMilSec === 0;
          if (isAllDay && !props.isIncludeAllDay) return false;

          // HostDiagnosticMode: バッファなしで日程が重なっているかcheck
          if (
            props.isHostDiagnosticMode &&
            isUnavailableSchedule(
              blockStartTime,
              blockEndTime,
              currentStart,
              currentEnd,
            )
          ) {
            if (
              block.unavailableInfomation &&
              block.transparency !== Transparency.Transparent
            ) {
              unavailableInfomations.push(block.unavailableInfomation);
            }
            if (block.transparency === Transparency.Transparent) {
              item[2] = convertBlogkToTransparentCalendarEvent(block);
              item[6] = block.transparency;
            }
            return true;
          }

          // バッファ込みのコマが別の予定と被っているか
          if (
            isUnavailableSchedule(
              blockStartTime,
              blockEndTime,
              props.isBufferResource
                ? currentWithBufferStartTime
                : currentStart,
              props.isBufferResource ? currentWithBufferEndTime : currentEnd,
            ) &&
            block.transparency !== Transparency.Transparent
          ) {
            if (props.isHostDiagnosticMode) {
              unavailableInfomations.push({
                reason: CalendarUnavailableReasonType.Buffer,
                users: [],
              });
            }
            return true;
          }
        });

        if (!isNotAvailable || remainingCapacity === 0) {
          isAnyResourceAvailable = true;
          if (isReturnAvailableResources) {
            item[8] = resourceSchedule.resources.map((r) => r.id!);
          } else {
            break;
          }
        }
      }
    } else {
      for (const blocks of adminBlocksGroup) {
        const isNotAvailable = blocks.events.some(
          (block: AdminCalendarEventListItem) => {
            let blockStartTime = Date.parse(block.startedAt);
            let blockEndTime = Date.parse(block.endedAt);
            const isAllDay = block.isAllDay;
            if (isAllDay) {
              if (!props.isIncludeAllDay) return;
              blockStartTime = formatToMills(
                getDateTime(block.startedAt).plus({
                  minute: utcOffsetSec,
                }),
              );
              blockEndTime = formatToMills(
                getDateTime(block.endedAt).plus({
                  minute: utcOffsetSec,
                }),
              );
            }

            if (props.isHostDiagnosticMode) {
              // 予約可能時間確認モードでバッファなしで日程が重なっているかcheck
              if (
                isUnavailableSchedule(
                  blockStartTime,
                  blockEndTime,
                  currentStart,
                  currentEnd,
                )
              ) {
                if (
                  block.unavailableInfomation &&
                  block.transparency !== Transparency.Transparent
                ) {
                  unavailableInfomations.push(block.unavailableInfomation);
                }
                if (block.transparency === Transparency.Transparent) {
                  item[2] = convertBlogkToTransparentCalendarEvent(block);
                  item[6] = block.transparency;
                  return false;
                }
                return true;
              }
              // 予約可能時間確認モードでバッファありで日程が重なっているかcheck
              if (
                !block.isBuffer &&
                isUnavailableSchedule(
                  blockStartTime,
                  blockEndTime,
                  currentWithBufferStartTime,
                  currentWithBufferEndTime,
                ) &&
                block.transparency !== Transparency.Transparent
              ) {
                unavailableInfomations.push({
                  reason: CalendarUnavailableReasonType.Buffer,
                  users: [],
                });
                return true;
              }
            }

            const isEventTypeEvent = block.eventTypeId === props.eventType.id;
            // バッファなしで日程が重なっているか
            if (
              isUnavailableSchedule(
                blockStartTime,
                blockEndTime,
                currentStart,
                currentEnd,
              ) &&
              block.transparency !== Transparency.Transparent
            ) {
              if (isEventTypeEvent) {
                remainingCapacity -= 1;
              }
              return true;
            }
            // バッファありで日程が重なっているか
            if (
              !block.isBuffer &&
              isUnavailableSchedule(
                blockStartTime,
                blockEndTime,
                currentWithBufferStartTime,
                currentWithBufferEndTime,
              ) &&
              block.transparency !== Transparency.Transparent
            ) {
              if (isEventTypeEvent) {
                remainingCapacity -= 1;
              }
              return true;
            }
          },
        );

        if (!isNotAvailable || remainingCapacity === 0) {
          isAnyAvailable = true;
          if (isReturnAvailableUsers) {
            item[7].push(blocks.userId);
          } else {
            break;
          }
        }
      }

      for (const resourceSchedule of props.resourceSchedules) {
        const isNotAvailable = resourceSchedule.events.some(
          (block: AdminCalendarEventListItem) => {
            let blockStartTime = Date.parse(block.startedAt);
            let blockEndTime = Date.parse(block.endedAt);
            const isAllDay = block.isAllDay;
            if (isAllDay) {
              if (!props.isIncludeAllDay) return;
              blockStartTime = formatToMills(
                getDateTime(block.startedAt).plus({
                  minute: utcOffsetSec,
                }),
              );
              blockEndTime = formatToMills(
                getDateTime(block.endedAt).plus({
                  minute: utcOffsetSec,
                }),
              );
            }

            if (props.isHostDiagnosticMode) {
              // 予約可能時間確認モードでバッファなしで日程が重なっているかcheck
              if (
                isUnavailableSchedule(
                  blockStartTime,
                  blockEndTime,
                  currentStart,
                  currentEnd,
                )
              ) {
                unavailableInfomations.push({
                  reason: CalendarUnavailableReasonType.Resource,
                  users: [],
                });
                remainingCapacity -= 1;
                return true;
              }
              // 予約可能時間確認モードでバッファありで日程が重なっているかcheck
              if (props.isBufferResource) {
                if (
                  isUnavailableSchedule(
                    blockStartTime,
                    blockEndTime,
                    currentWithBufferStartTime,
                    currentWithBufferEndTime,
                  ) &&
                  block.transparency !== Transparency.Transparent
                ) {
                  unavailableInfomations.push({
                    reason: CalendarUnavailableReasonType.Resource,
                    users: [],
                  });
                  return true;
                }
              }
            }

            // バッファありで日程が重なっているか
            if (
              isUnavailableSchedule(
                blockStartTime,
                blockEndTime,
                props.isBufferResource
                  ? currentWithBufferStartTime
                  : currentStart,
                props.isBufferResource ? currentWithBufferEndTime : currentEnd,
              ) &&
              block.transparency !== Transparency.Transparent
            ) {
              return true;
            }
          },
        );

        if (!isNotAvailable) {
          isAnyResourceAvailable = true;
          if (isReturnAvailableResources) {
            item[8] = resourceSchedule.resources.map((r) => r.id!);
          } else {
            break;
          }
        }
      }
    }

    if (!isAnyResourceAvailable) {
      remainingCapacity = 0;
    }
    if (
      (!isAnyAvailable || !isAnyResourceAvailable) &&
      !props.isHostDiagnosticMode
    ) {
      continue;
    }

    let viewerBlock: ViewerCalendarEventListItem | null = null;
    viewerBlocks.some((block) => {
      const startedAt = block.isAllDay
        ? getDateTime(block.startedAt).plus({
            minute: utcOffsetSec,
          })
        : block.startedAt;
      const endedAt = block.isAllDay
        ? getDateTime(block.endedAt).plus({
            minute: utcOffsetSec,
          })
        : block.endedAt;

      const blockStartTime = formatToMills(startedAt);
      const blockEndTime = formatToMills(endedAt);
      if (blockEndTime > currentStart && currentStart >= blockStartTime) {
        viewerBlock = block;
        return true;
      }
      // if (blockEndTime > currentEnd && currentEnd >= blockStartTime) {
      //   name = block.name;
      //   return true;
      // }
    });

    if (viewerBlock) {
      viewerBlock = viewerBlock as ViewerCalendarEventListItem;
      if (item[2]) {
        item[2].name = viewerBlock.name ?? '';
      } else {
        item[2] = {
          name: viewerBlock.name ?? '',
          color: viewerBlock.color ?? '',
          uid: viewerBlock.uid ?? '',
          userId: viewerBlock.userId ?? '',
          accountId: viewerBlock.accountId ?? null,
          calendarId: viewerBlock.calendarId ?? '',
        };
      }
    }

    if (unavailableInfomations.length > 0 && !isAnyAvailable) {
      item[5] = unavailableInfomations;
    }

    if (hasCapacity) {
      if (props.eventType.isDisplayRemainingCapacity) {
        item[3] = remainingCapacity;
      }
      if (props.eventType.isRevealGuestUser) {
        item[4] = guestUsers;
      }
    }
    if (remainingCapacity === 0) {
      if (isDisplayFullCapacity) {
        item[3] = 0;
      }
      if (props.isHostDiagnosticMode) {
        const hasUnavailableReasonTypeResource = unavailableInfomations.find(
          (e) => e.reason === CalendarUnavailableReasonType.Resource,
        );
        item[5] = [
          {
            reason: hasUnavailableReasonTypeResource
              ? CalendarUnavailableReasonType.Resource
              : CalendarUnavailableReasonType.Event,
            users: [],
          },
        ];
      }
      if (!props.isHostDiagnosticMode && !isDisplayFullCapacity) {
        continue;
      }
    }

    timeList.push(item);
  }

  return timeList;
};

function filterBlock(
  block: ViewerCalendarEventListItem | AdminCalendarEventListItem,
  timeZone: string,
  selectedStartTime: number,
) {
  const blockStartTime = new Date(block.startedAt).getTime();
  const blockEndTime = new Date(block.endedAt).getTime();

  if (blockEndTime < selectedStartTime && selectedStartTime >= blockStartTime) {
    return false;
  }
  return true;
}

function processToLocaltime(
  intervalMinutes: number,
  eventTypeCalendars: EventTypeCalendar[],
  offsetMills: number,
  localTimeZone: string,
  eventTypeTimeZone: string,
  selectedDateTime: DateTime,
): LocalEventTypeCalendars[] {
  const localEventTypeCalendars: LocalEventTypeCalendars[] = [];
  const selectedDateLocalOffset = selectedDateTime.setZone(localTimeZone)
    .offset;
  const selectedDateEventTypeOffset = selectedDateTime.setZone(
    eventTypeTimeZone,
  ).offset;
  for (const calendar of eventTypeCalendars) {
    const startedAtDateLocalOffset = getDateTime(calendar.startedAt).setZone(
      localTimeZone,
    ).offset;
    const startedAtDateEventTypeOffset = getDateTime(
      calendar.startedAt,
    ).setZone(eventTypeTimeZone).offset;
    // 予約ページタイムゾーンの設定開始日と表示する日のどちらかが夏時間の場合diffが出る
    const eventTypeTimeDiffMinute =
      selectedDateEventTypeOffset - startedAtDateEventTypeOffset;
    const isEventTypeTimezoneAdvanced = eventTypeTimeDiffMinute > 0;
    const isEventTypeTimezoneDelayed = eventTypeTimeDiffMinute < 0;
    // ローカルタイムゾーンの設定開始日と表示する日のどちらかが夏時間の場合diffが出る
    const localTimeDiffMinute =
      selectedDateLocalOffset - startedAtDateLocalOffset;
    const islocalTimezoneAdvanced = localTimeDiffMinute > 0;
    const islocalTimezoneDelayed = localTimeDiffMinute < 0;

    if (offsetMills === 0) {
      localEventTypeCalendars.push({
        id: calendar.id!,
        startedAt: calendar.startedAt,
        endedAt: calendar.endedAt,
        startHour: calendar.startHour,
        startMinute: calendar.startMinute,
        endHour:
          calendar.endHour === 0 && calendar.endMinute === 0
            ? 24
            : calendar.endHour,
        endMinute: calendar.endMinute,
        wday: calendar.wday,
        frequencyType: calendar.frequencyType,
        frequencyInterval: calendar.frequencyInterval,
        parentEventTypeCalendarId: calendar.parentEventTypeCalendarId,
        exceptions: calendar.exceptions,
      });
      continue;
    }

    let eventTypeStartedAt = getDateTime(calendar.startedAt)
      .setZone(eventTypeTimeZone)
      .set({
        weekday: calendar.wday % 7,
        hour: calendar.startHour,
        minute: calendar.startMinute,
      });
    let eventTypeEndedAt = getDateTime(calendar.startedAt)
      .setZone(eventTypeTimeZone)
      .set({
        weekday: calendar.wday % 7,
        hour: calendar.endHour,
        minute: calendar.endMinute,
      });

    if (isEventTypeTimezoneAdvanced) {
      eventTypeStartedAt = eventTypeStartedAt.minus({
        minute: Math.abs(eventTypeTimeDiffMinute),
      });
      eventTypeEndedAt = eventTypeEndedAt.minus({
        minute: Math.abs(eventTypeTimeDiffMinute),
      });
    } else if (isEventTypeTimezoneDelayed) {
      eventTypeStartedAt = eventTypeStartedAt.plus({
        minute: Math.abs(eventTypeTimeDiffMinute),
      });
      eventTypeEndedAt = eventTypeEndedAt.plus({
        minute: Math.abs(eventTypeTimeDiffMinute),
      });
    }
    let localStartedAt = eventTypeStartedAt.setZone(localTimeZone);
    let localEndedAt = eventTypeEndedAt.setZone(localTimeZone);

    if (islocalTimezoneAdvanced) {
      localStartedAt = localStartedAt.plus({
        minute: Math.abs(localTimeDiffMinute),
      });
      localEndedAt = localEndedAt.plus({
        minute: Math.abs(localTimeDiffMinute),
      });
    } else if (islocalTimezoneDelayed) {
      localStartedAt = localStartedAt.minus({
        minute: Math.abs(localTimeDiffMinute),
      });
      localEndedAt = localEndedAt.minus({
        minute: Math.abs(localTimeDiffMinute),
      });
    }

    // タイムゾーンを変更して日またぎになった場合、またぐ日の終了時間+インターバル分を足した時間から開始する
    let timeMinute = localStartedAt.hour * 60 + localStartedAt.minute;
    while (timeMinute < 1440) {
      timeMinute = timeMinute + intervalMinutes;
    }
    const endDayStartHour = Math.floor(timeMinute / 60) % 24;
    const endDayStartMinute = timeMinute % 60;

    const localStartDayExceptions: string[] = [];
    const localEndDayExceptions: string[] = [];
    if (calendar.exceptions?.length > 0) {
      calendar.exceptions.forEach((exception: string) => {
        // timezoneの変更により日付が変わるかどうか
        if (localStartedAt.weekday % 7 === calendar.wday) {
          localStartDayExceptions.push(exception);
          localEndDayExceptions.push(exception);
        } else {
          const orgDateTime = getDateTime(exception).setZone(localTimeZone);
          if (localStartedAt.weekday > calendar.wday) {
            // ローカルの日付が進む場合
            localStartDayExceptions.push(formatToEXDATE(orgDateTime));
            localEndDayExceptions.push(
              formatToEXDATE(orgDateTime.plus({ day: 1 })),
            );
          } else {
            // ローカルの日付が遅れる場合
            localStartDayExceptions.push(
              formatToEXDATE(orgDateTime.minus({ day: 1 })),
            );
            localEndDayExceptions.push(formatToEXDATE(orgDateTime));
          }
        }
      });
    }
    if (calendar.frequencyType === null) {
      if (localStartedAt.day === localEndedAt.day) {
        localEventTypeCalendars.push({
          id: calendar.id!,
          startedAt: calendar.startedAt,
          endedAt: calendar.endedAt,
          startHour: localStartedAt.hour,
          startMinute: localStartedAt.minute,
          endHour: localEndedAt.hour,
          endMinute: localEndedAt.minute,
          wday: localStartedAt.weekday % 7,
          frequencyType: calendar.frequencyType,
          frequencyInterval: calendar.frequencyInterval,
          parentEventTypeCalendarId: calendar.parentEventTypeCalendarId,
          exceptions: localStartDayExceptions,
        });
      } else {
        localEventTypeCalendars.push(
          {
            id: calendar.id!,
            startedAt: calendar.startedAt,
            endedAt: calendar.endedAt,
            startHour: localStartedAt.hour,
            startMinute: localStartedAt.minute,
            endHour: 24 + localEndedAt.hour,
            endMinute: localEndedAt.minute,
            wday: localStartedAt.weekday % 7,
            frequencyType: calendar.frequencyType,
            frequencyInterval: calendar.frequencyInterval,
            parentEventTypeCalendarId: calendar.parentEventTypeCalendarId,
            exceptions: localStartDayExceptions,
          },
          {
            id: calendar.id!,
            startedAt: new Date(formatToMills(localEndedAt.startOf('day'))),
            endedAt: calendar.endedAt,
            startHour: endDayStartHour,
            startMinute: endDayStartMinute,
            endHour: localEndedAt.hour,
            endMinute: localEndedAt.minute,
            wday: localEndedAt.weekday % 7,
            frequencyType: calendar.frequencyType,
            frequencyInterval: calendar.frequencyInterval,
            parentEventTypeCalendarId: calendar.parentEventTypeCalendarId,
            exceptions: localEndDayExceptions,
          },
        );
      }
      continue;
    }

    if (localStartedAt.day === localEndedAt.day) {
      localEventTypeCalendars.push({
        id: calendar.id!,
        startedAt: calendar.startedAt,
        endedAt: calendar.endedAt,
        startHour: localStartedAt.hour,
        startMinute: localStartedAt.minute,
        endHour: localEndedAt.hour,
        endMinute: localEndedAt.minute,
        wday: localStartedAt.weekday % 7,
        frequencyType: calendar.frequencyType,
        frequencyInterval: calendar.frequencyInterval,
        parentEventTypeCalendarId: calendar.parentEventTypeCalendarId,
        exceptions: localStartDayExceptions,
      });
    } else {
      localEventTypeCalendars.push(
        {
          id: calendar.id!,
          startedAt: calendar.startedAt,
          endedAt: calendar.endedAt,
          startHour: localStartedAt.hour,
          startMinute: localStartedAt.minute,
          endHour: 24 + localEndedAt.hour,
          endMinute: localEndedAt.minute,
          wday: localStartedAt.weekday % 7,
          frequencyType: calendar.frequencyType,
          frequencyInterval: calendar.frequencyInterval,
          parentEventTypeCalendarId: calendar.parentEventTypeCalendarId,
          exceptions: localStartDayExceptions,
        },
        {
          id: calendar.id!,
          startedAt: calendar.startedAt,
          endedAt: calendar.endedAt,
          startHour: endDayStartHour,
          startMinute: endDayStartMinute,
          endHour: localEndedAt.hour,
          endMinute: localEndedAt.minute,
          wday: localEndedAt.weekday % 7,
          frequencyType: calendar.frequencyType,
          frequencyInterval: calendar.frequencyInterval,
          parentEventTypeCalendarId: calendar.parentEventTypeCalendarId,
          exceptions: localEndDayExceptions,
        },
      );
    }
  }

  return localEventTypeCalendars;
}

function rangeStatus(): UnavailableInfomation[] {
  return [
    {
      reason: CalendarUnavailableReasonType.Range,
      users: [],
    },
  ];
}

function convertBlogkToTransparentCalendarEvent(
  block: AdminCalendarEventListItem,
): TimeListViewerCalendarEvent {
  return {
    name: block.eventName ?? null,
    uid: block.uid,
    userId: block.userId ?? null,
    accountId: block.accountId ?? null,
    calendarId: block.calendarId ?? null,
    color: block.color ?? null,
  };
}

function isUnavailableSchedule(
  blockStartTime: number,
  blockEndTime: number,
  currentStart: number,
  currentEnd: number,
) {
  if (
    (blockEndTime > currentStart && currentStart >= blockStartTime) ||
    (blockEndTime > currentEnd && currentEnd > blockStartTime) ||
    (currentStart <= blockStartTime && blockEndTime <= currentEnd)
  ) {
    return true;
  }
  return false;
}

function getTimeSlots(
  range: {
    startHour: number;
    startMinute: number;
    endHour: number;
    endMinute: number;
  },
  intervalMinutes: number,
  eventTypeVersion: EventTypeVersion,
) {
  const { startHour, startMinute, endHour, endMinute } = range;
  const slots: { hour: number; minute: number }[] = [];

  if (eventTypeVersion === EventTypeVersion.V1) {
    const maxM = 60 / intervalMinutes;
    for (let hour = startHour; hour <= endHour; hour++) {
      for (let m = 0; m < maxM; m++) {
        const minute = m * intervalMinutes;
        if (hour === endHour && minute > endMinute) {
          break;
        }
        slots.push({ hour, minute });
      }
    }
  } else {
    let currentHour = startHour;
    let currentMinute = startMinute;
    while (true) {
      slots.push({ hour: currentHour, minute: currentMinute });
      currentMinute += intervalMinutes;
      if (currentMinute >= 60) {
        currentHour += Math.floor(currentMinute / 60);
        currentMinute %= 60;
      }

      if (
        currentHour > endHour ||
        (currentHour === endHour && currentMinute >= endMinute)
      ) {
        break;
      }
    }
  }

  return slots;
}

function getAvailableRange(
  selectedDateTime: DateTime,
  requestTimeZone: string,
  eventTypeCalendars: LocalEventTypeCalendars[],
): {
  startHour: number;
  startMinute: number;
  endHour: number;
  endMinute: number;
}[] {
  const currentDay = selectedDateTime
    .minus({
      // 現在時刻ではなくselectedDateTimeを利用しているのはサマータイムの影響を考慮するため
      minutes: getDateTime(selectedDateTime).setZone(requestTimeZone).offset,
    })
    .setZone(requestTimeZone)
    .startOf('day');

  const year = selectedDateTime.year;
  const month = selectedDateTime.month;
  const day = selectedDateTime.day;
  const filteredCalendars = eventTypeCalendars.filter((calendar) => {
    // 対象外の曜日
    if (calendar.wday !== selectedDateTime.weekday % 7) {
      return false;
    }

    // 繰り返しが2週以上で対象外の週ならskip
    if (
      calendar.frequencyType === FrequencyType.Weekly &&
      calendar.frequencyInterval &&
      calendar.frequencyInterval > 1
    ) {
      // 検査日と予約可能設定の開始日の差分が何週分か調べる
      const diffWeeks = Math.floor(
        getDateTime(formatToMills(currentDay)).diff(
          getDateTime(calendar.startedAt),
          'weeks',
        ).weeks,
      );
      if (diffWeeks % calendar.frequencyInterval) {
        return false;
      }
    }

    // その週のみ削除の場合
    if (calendar.exceptions && calendar.exceptions.length > 0) {
      for (const exception of calendar.exceptions) {
        if (
          year === getDateTime(exception).year &&
          month === getDateTime(exception).month &&
          day === getDateTime(exception).day
        ) {
          return false;
        }
      }
    }

    // 検証してるレコードがこの週のみ変更された親レコードの場合は子レコードの時間を採用する
    let shoudUseChild = false;
    const childEventTypeCalendars = eventTypeCalendars.filter(
      (c: LocalEventTypeCalendars) =>
        c.parentEventTypeCalendarId === calendar.id,
    );
    for (const child of childEventTypeCalendars) {
      if (
        year === getDateTime(child.startedAt).year &&
        month === getDateTime(child.startedAt).month &&
        day === getDateTime(child.startedAt).day
      ) {
        shoudUseChild = true;
        break;
      }
    }
    if (shoudUseChild) {
      return false;
    }

    // EventTypeCalendarの開始時間帯が検査日の終了より後か
    const currentDayEnd = formatToMills(currentDay.endOf('day'));
    const calendarStartTime = formatToMills(
      getDateTime(calendar.startedAt, requestTimeZone).set({
        hour: calendar.startHour,
        minute: calendar.startMinute,
      }),
    );
    if (calendarStartTime > currentDayEnd) {
      return false;
    }

    // EventTypeCalendarの終了時間帯が検査日の開始より前か
    if (calendar.endedAt) {
      const currentDayStart = formatToMills(currentDay.startOf('day'));
      const calendarEndTime = formatToMills(
        getDateTime(calendar.endedAt, requestTimeZone).set({
          hour: calendar.endHour,
          minute: calendar.endMinute,
        }),
      );
      if (calendarEndTime <= currentDayStart) {
        return false;
      }
    }

    return true;
  });

  if (filteredCalendars.length === 0) {
    return [];
  }

  const ranges = filteredCalendars
    .map((calendar) => {
      const { startHour, startMinute, endHour, endMinute } = calendar;
      return {
        startHour,
        startMinute,
        endHour: endHour > 23 ? 24 : endHour,
        endMinute: endHour > 23 ? 0 : endMinute,
      };
    })
    .sort(
      (a, b) =>
        a.startHour * 60 + a.startMinute - (b.startHour * 60 + b.startMinute),
    );

  const merged = [ranges[0]];
  for (let i = 1; i < ranges.length; i++) {
    const lastMerged = merged[merged.length - 1];
    const current = ranges[i];

    if (
      current.startHour * 60 + current.startMinute <=
      lastMerged.endHour * 60 + lastMerged.endMinute
    ) {
      if (
        current.endHour * 60 + current.endMinute >
        lastMerged.endHour * 60 + lastMerged.endMinute
      ) {
        lastMerged.endHour = current.endHour;
        lastMerged.endMinute = current.endMinute;
      }
    } else {
      merged.push(current);
    }
  }

  return merged;
}

export function convertUserTypeToText(
  userType: UserType,
): UserTypeText.Host | UserTypeText.NotifyHost {
  switch (userType) {
    case UserType.NotifyHost:
      return UserTypeText.NotifyHost;
    default:
      return UserTypeText.Host;
  }
}

export function convertRoleToText(role: Role): RoleText {
  switch (role) {
    case Role.Admin:
      return RoleText.Admin;
    case Role.Editor:
      return RoleText.Editor;
    case Role.Viewer:
      return RoleText.Viewer;
  }
}
